import { Fragment, useEffect, useRef, type ReactNode } from 'react';
import { formatDateTime } from '../../../shared/src';
import { AuditLog } from '../api';

interface AuditLogDetailsPanelProps {
  log: AuditLog;
  onClose: () => void;
}

function formatDetailValue(value: unknown): ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="side-panel-muted">-</span>;
  }
  // Flat lists (e.g. project_ids) read better as chips than as a JSON block.
  if (Array.isArray(value) && value.every((item) => item === null || typeof item !== 'object')) {
    if (value.length === 0) return <span className="side-panel-muted">(empty)</span>;
    return value.map((item, i) => <code key={i}>{String(item)}</code>);
  }
  if (typeof value === 'object') {
    return <pre className="side-panel-json">{JSON.stringify(value, null, 2)}</pre>;
  }
  return String(value);
}

/**
 * Slide-in panel showing everything stored on one audit log row, including
 * the free-form `details` JSON the table has no column for. Clicking the
 * backdrop or pressing Escape closes it.
 */
export function AuditLogDetailsPanel({ log, onClose }: AuditLogDetailsPanelProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const detailEntries = Object.entries(log.details ?? {});

  return (
    <div className="side-panel-overlay" onClick={onClose}>
      <aside
        className="side-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-log-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="side-panel-header">
          <div>
            <h2 id="audit-log-panel-title">
              <code>{log.action}</code>
            </h2>
            <div className="side-panel-subtitle">
              {formatDateTime(log.created_at, { withZone: true })}
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="side-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="side-panel-body">
          <dl className="side-panel-grid">
            <dt>Time (UTC)</dt>
            <dd>{new Date(log.created_at).toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC')}</dd>

            <dt>Organization</dt>
            <dd>
              {log.organization_name || <span className="side-panel-muted">-</span>}
              {log.organization_id && <code>{log.organization_id}</code>}
            </dd>

            {log.user_email && (
              <>
                <dt>User</dt>
                <dd>{log.user_email}</dd>
              </>
            )}

            {log.admin_email && (
              <>
                <dt>Admin</dt>
                <dd>
                  {log.admin_name && <span>{log.admin_name}</span>}
                  <span className={log.admin_name ? 'side-panel-muted' : undefined}>{log.admin_email}</span>
                </dd>
              </>
            )}

            {!log.user_email && !log.admin_email && (
              <>
                <dt>User</dt>
                <dd className="side-panel-muted">-</dd>
              </>
            )}

            <dt>Resource</dt>
            <dd>
              {log.resource_type || <span className="side-panel-muted">-</span>}
              {log.resource_id && <code>{log.resource_id}</code>}
            </dd>

            <dt>IP Address</dt>
            <dd>{log.ip_address || <span className="side-panel-muted">-</span>}</dd>

            <dt>User Agent</dt>
            <dd>{log.user_agent || <span className="side-panel-muted">-</span>}</dd>
          </dl>

          <h3>Details</h3>
          {detailEntries.length === 0 ? (
            <p className="side-panel-muted">No details recorded</p>
          ) : (
            <dl className="side-panel-grid side-panel-details">
              {detailEntries.map(([key, value]) => (
                <Fragment key={key}>
                  <dt>{key}</dt>
                  <dd>{formatDetailValue(value)}</dd>
                </Fragment>
              ))}
            </dl>
          )}
        </div>
      </aside>
    </div>
  );
}
